import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FiCheck, FiCheckCircle, FiFileText, FiX } from "react-icons/fi";
import { ModalFrame, T, btnStyleOutline, localToday } from "../../_components/Sales/SalesShared";
import { createDeliveryOrder, getSalesOrderById } from "../../_services/sales.service";

/* "Create DO" for several invoices at once on the Invoice list. Each Delivery
   Order is filled exactly like the Create DO button on an invoice fills the
   form: from the invoice's Sales Order, with the invoice as its reference and
   the Sales Order's Delivery Date. The server checks each one as usual (SO and
   invoice links, one active DO per SO, Approved only for an Approved invoice). */

// Same rule as the Create DO button on the invoice page. The server sends
// `deliveryOrderBlock` when the invoice, or its Sales Order, already has a DO.
export const canCreateDeliveryOrder = (row) =>
    row.status === "Approved" && Boolean(row.soRef || row.so_ref) && !row.deliveryOrderBlock;

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// The Delivery Order the invoice's Create DO form would save.
export function deliveryOrderFromSalesOrder(invoice, so, status) {
    return {
        date: localToday(),
        deliveryDate: so.deliveryDate || so.delivery_date || null,
        soRef: so.id || so.so_id,
        invoiceRef: invoice.id,
        custPONo: so.custPONo || so.cust_po_no || null,
        customer: so.customer,
        contactPerson: so.contactPerson || so.contact_person || null,
        phoneNumber: so.phoneNumber || so.phone_number || null,
        customerAddress: so.customerAddress || so.customer_address || null,
        shippingAddress: so.shippingAddress || so.shipping_address || null,
        paymentTerms: so.paymentTerms || so.payment_terms || null,
        currency: so.currency || "MYR",
        status,
        notes: null,
        items: (so.items || []).filter((item) => item.selected !== false).map((item) => ({ ...item, selected: true })),
    };
}

async function createOne(invoice, status) {
    const soRef = invoice.soRef || invoice.so_ref;
    const so = await getSalesOrderById(soRef);
    if (!so) throw new Error(`Sales Order ${soRef} was not found.`);
    if (!(so.deliveryDate || so.delivery_date)) {
        throw new Error(`Sales Order ${soRef} has no Delivery Date. Create this DO from the invoice page.`);
    }
    return createDeliveryOrder(deliveryOrderFromSalesOrder(invoice, so, status));
}

export function describeCreateOutcome({ created, failed }) {
    const parts = [];
    if (created.length) {
        parts.push(
            `${plural(created.length, "Delivery Order")} created: ` +
            created.map(({ invoice, deliveryOrder }) => `${deliveryOrder.id} (from ${invoice.id})`).join(", ")
        );
    }
    if (failed.length) {
        parts.push(
            `${plural(failed.length, "invoice")} failed: ` +
            failed.map(({ invoice, error }) => `${invoice.id} (${error?.message || "unknown error"})`).join("; ")
        );
    }
    return { type: failed.length ? "danger" : "success", msg: `${parts.join(". ")}.` };
}

/* The "Create DO" button in the list's selection bar, plus its dialog.
   `onDone({ type, msg })` reports the outcome; the list then reloads. */
export function BulkCreateDeliveryOrders({ selectedRows, onDone }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const inFlight = useRef(false);
    const mounted = useRef(true);
    const [targets, setTargets] = useState([]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const eligible = selectedRows.filter(canCreateDeliveryOrder);
    const skipped = selectedRows.length - eligible.length;

    const start = () => {
        setTargets(eligible);
        setOpen(true);
    };

    const run = async (status) => {
        if (inFlight.current || !targets.length) return;
        inFlight.current = true;
        setBusy(true);
        setProgress(0);
        const created = [];
        const failed = [];
        // One after another: two invoices of the same Sales Order must not race
        // for its single active Delivery Order.
        for (const [index, invoice] of targets.entries()) {
            try {
                created.push({ invoice, deliveryOrder: await createOne(invoice, status) });
            } catch (error) {
                failed.push({ invoice, error });
            }
            if (mounted.current) setProgress(index + 1);
        }
        inFlight.current = false;
        if (mounted.current) {
            setBusy(false);
            setOpen(false);
        }
        onDone(describeCreateOutcome({ created, failed }));
    };

    const close = () => {
        if (!busy) setOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={start}
                disabled={eligible.length === 0}
                title={eligible.length === 0
                    ? "Delivery Orders can be created only from Approved invoices that have a Sales Order and no Delivery Order yet"
                    : "Create a Delivery Order from each selected invoice's Sales Order"}
                style={{
                    ...btnStyleOutline,
                    opacity: eligible.length === 0 ? 0.5 : 1,
                    cursor: eligible.length === 0 ? "not-allowed" : "pointer",
                }}
            >
                <FiFileText size={13} /> Create DO{eligible.length ? ` (${eligible.length})` : ""}
            </button>

            {open && ReactDOM.createPortal(
                <ModalFrame onCancel={close} labelledBy="bulk-create-do-title">
                    <button className="cl-modal-close" onClick={close} disabled={busy} aria-label="Close dialog"><FiX size={14} /></button>
                    <div className="cl-modal-icon" style={{ background: "#eff6ff", color: T.accent }}>
                        <FiFileText size={26} />
                    </div>
                    <p className="cl-modal-msg" id="bulk-create-do-title">
                        {busy
                            ? <>Creating Delivery Orders… <strong>{progress} of {targets.length}</strong></>
                            : <>Create Delivery Orders for <strong>{plural(targets.length, "invoice")}</strong>?</>}
                    </p>
                    <p className="cl-modal-msg" style={{ fontSize: 13, marginTop: -8 }}>
                        Each DO is filled from the invoice's Sales Order and uses that Sales Order's Delivery Date.
                        {skipped > 0 && ` ${plural(skipped, "selected invoice")} will be left out (not Approved, no Sales Order, or already has a Delivery Order).`}
                    </p>
                    <div className="cl-modal-actions" style={{ flexWrap: "wrap" }}>
                        <button className="cl-modal-btn-cancel" onClick={close} disabled={busy}>Cancel</button>
                        <button
                            className="cl-modal-btn-confirm"
                            style={{ background: T.accent }}
                            onClick={() => run("Draft")}
                            disabled={busy}
                        >
                            <FiCheck size={13} /> Save as Draft
                        </button>
                        <button
                            className="cl-modal-btn-confirm cl-modal-btn-confirm-success"
                            onClick={() => run("Approved")}
                            disabled={busy}
                        >
                            <FiCheckCircle size={13} /> Save & Approve
                        </button>
                    </div>
                </ModalFrame>,
                document.body
            )}
        </>
    );
}
